import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Skeleton body for `DataTable` while `loading` is true. Renders `rows` ×
 * `columns` skeleton cells over the same grid template as the real body so the
 * layout doesn't shift when data arrives.
 */
export function DataTableLoadingRows({
  rows,
  columns,
  gridTemplate,
  announce = true,
  dense = false,
}: {
  rows: number;
  columns: number;
  gridTemplate: string;
  /**
   * Set `false` when nesting inside a consumer that already owns a live
   * region for this same loading state (e.g. `TableRefetchOverlay`'s
   * "Refreshing" status, `AuditLogsPendingState`'s page-level `aria-live`)
   * — otherwise two differently-labelled live regions announce one event.
   */
  announce?: boolean;
  /** Tighter vertical padding — see `DataTable`'s `dense` prop. */
  dense?: boolean;
}) {
  return (
    // `role="rowgroup"` is the direct parent of the `role="row"` children
    // below, so they satisfy `aria-required-parent` (row's required parent is
    // grid/rowgroup/table/treegrid). `rowgroup` itself only allows `row`
    // children (`aria-required-children`), so the loading announcement can't
    // be a separate `role="status"` sibling in here either — `aria-live`
    // doesn't require `role="status"`, it works on any role, so the rowgroup
    // is the live region directly via `aria-live`/`aria-label`.
    <div
      role="rowgroup"
      {...(announce
        ? { 'aria-live': 'polite' as const, 'aria-busy': 'true', 'aria-label': 'Loading rows' }
        : {})}
    >
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          role="row"
          className={cn('grid items-center gap-3 border-b px-3 last:border-b-0', dense ? 'py-2' : 'py-3')}
          style={{
            gridTemplateColumns: gridTemplate,
            borderColor: 'var(--ink-100)',
          }}
        >
          {Array.from({ length: columns }).map((__, colIdx) => (
            <Skeleton
              key={colIdx}
              role="cell"
              className="h-4 w-full"
              data-testid="datatable-skeleton-cell"
            />
          ))}
        </div>
      ))}
    </div>
  );
}
